import { ApiErrorBody } from '@mtg/shared';
import type { z } from 'zod';

export class ApiError extends Error {
  constructor(
    /** HTTP status, or 0 when the server couldn't be reached at all. */
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

export const isOffline = (e: unknown) => e instanceof ApiError && e.status === 0;

/** Calls the API, validating the response with `schema`. A body makes it a POST unless `method` says otherwise. */
export async function api<T extends z.ZodType>(
  path: string,
  schema: T,
  init: { method?: string; body?: unknown } = {},
): Promise<z.infer<T>> {
  const hasBody = init.body !== undefined;
  let res: Response;
  try {
    res = await fetch(path, {
      method: init.method ?? (hasBody ? 'POST' : 'GET'),
      headers: hasBody ? { 'content-type': 'application/json' } : undefined,
      body: hasBody ? JSON.stringify(init.body) : undefined,
      credentials: 'same-origin',
    });
  } catch {
    throw new ApiError(0, 'offline', "Can't reach the app. Check your connection.");
  }
  const json: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const err = ApiErrorBody.safeParse(json);
    throw err.success
      ? new ApiError(res.status, err.data.error, err.data.message)
      : new ApiError(res.status, 'http_error', `Request failed (${res.status}).`);
  }
  return schema.parse(json);
}

export const errorText = (e: unknown) => (e instanceof ApiError ? e.message : 'Something went wrong.');
